/**
 * Pagina portale job Prefect — vista /jobs con live polling.
 *
 * Per ogni job (deployment): l'ultima esecuzione (in corso o terminata), ordinata dalla
 * più recente; seguono i job mai eseguiti (placeholder). I job in corso sono evidenziati
 * in cima e i loro log sono live-pollati ogni 2s.
 *
 * Funzioni: filtro multi-flow (dropdown di checkbox con select/deselect all), espansione
 * log inline (più righe aperte contemporaneamente), ricerca testo nei log, tooltip con la
 * doc del flow (da prefect/flows), "Esegui ora" on-demand (admin).
 *
 * Deep link: ogni job ha un ID stabile (slug del deployment) e un URL `/jobs#<id>`; aprendolo la
 * riga viene portata in vista, evidenziata e ne vengono aperti i log. Il click sul nome del job
 * copia quell'URL negli appunti.
 */

import { RunDenyPopup } from './RunDenyPopup.js';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useHref, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/index.js';
import { copyToClipboard } from '../../components/CopyButton.js';
import { jobIdFromHash, jobIdsFor } from './job-links.js';
import { usePrefectJobRuns, usePrefectFlowRunLogs, useRunDeployment } from './hooks.js';
import { FLOW_DOCS } from './flow-docs.generated.js';
import type { FlowRun, JobRun, LogLine } from './types.js';
import styles from './Prefect.module.css';

const COL_COUNT = 6;

// ---------------------------------------------------------------------------
// Helper: badge colorato per stateType
// ---------------------------------------------------------------------------

function StateBadge({ stateType, label }: { stateType: string; label: string }) {
  const cls = (() => {
    switch (stateType.toUpperCase()) {
      case 'COMPLETED':
        return styles.badgeCompleted;
      case 'FAILED':
        return styles.badgeFailed;
      case 'CRASHED':
        return styles.badgeCrashed;
      case 'RUNNING':
        return styles.badgeRunning;
      case 'PENDING':
      case 'SCHEDULED':
        return styles.badgePending;
      case 'CANCELLED':
      case 'CANCELLING':
        return styles.badgeCancelled;
      case 'NEVER':
        return styles.badgeNever;
      default:
        return styles.badgeDefault;
    }
  })();
  return <span className={`${styles.badge} ${cls}`}>{label}</span>;
}

// ---------------------------------------------------------------------------
// Helper: formattazione date / run
// ---------------------------------------------------------------------------

function fmtDateTime(iso: string): string {
  return new Date(iso).toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/** Timestamp di avvio: effettivo se presente, altrimenti previsto (run schedulati). */
function orarioEsecuzione(run: FlowRun): string {
  const ts = run.startTime ?? run.expectedStartTime;
  return ts ? fmtDateTime(ts) : '-';
}

/** True se il run è collegato a un deployment ⇒ avviabile on-demand. */
function isEseguibileOnDemand(run: FlowRun): boolean {
  return Boolean(run.deploymentId);
}

function durata(start?: string, end?: string): string {
  if (!start || !end) return '-';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 0) return '-';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

// ---------------------------------------------------------------------------
// Nome del job: copia il deep link + tooltip con la doc (da prefect/flows)
// ---------------------------------------------------------------------------

/** Durata del feedback "Link copiato". */
const COPY_FEEDBACK_MS = 2500;

type EsitoCopia = 'idle' | 'copied' | 'manual';

/**
 * Nome del job come bottone: il click (o Invio/Spazio) copia negli appunti il deep link
 * `/jobs#<jobId>` e mostra «Link copiato». Se la copia non è possibile (niente Clipboard API né
 * execCommand) mostra l'URL selezionato da copiare a mano. Il tooltip con la doc del flow resta
 * su hover/focus.
 */
function JobNameCell({ name, jobId, url }: { name?: string; jobId: string; url: string }) {
  const [esito, setEsito] = useState<EsitoCopia>('idle');
  const manualRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (esito !== 'copied') return;
    const t = setTimeout(() => setEsito('idle'), COPY_FEEDBACK_MS);
    return () => clearTimeout(t);
  }, [esito]);

  useEffect(() => {
    if (esito === 'manual') manualRef.current?.select();
  }, [esito]);

  const label = name ?? jobId;
  const doc = name ? FLOW_DOCS[name] : undefined;

  async function copia(e: React.MouseEvent) {
    // La riga è cliccabile (apre i log): il click sul nome copia e basta.
    e.stopPropagation();
    try {
      await copyToClipboard(url);
      setEsito('copied');
    } catch {
      setEsito('manual');
    }
  }

  return (
    <span className={styles.jobNameCell}>
      <span
        className={doc ? styles.flowNameCell : undefined}
        data-testid={doc ? `flow-name-${name}` : undefined}
      >
        <button
          type="button"
          className={styles.jobLinkBtn}
          onClick={(e) => void copia(e)}
          aria-label={`${label} — copia il link a questo job`}
          title="Copia il link a questo job"
          data-testid={`job-link-${jobId}`}
        >
          {label}
        </button>
        {doc && (
          <span className={styles.flowDocTooltip} role="tooltip" data-testid={`flow-doc-${name}`}>
            {doc}
          </span>
        )}
      </span>
      {jobId !== label && (
        <span className={styles.jobId} data-testid={`job-id-${jobId}`}>
          #{jobId}
        </span>
      )}
      <span role="status" aria-live="polite" className={styles.copyFeedback}>
        {esito === 'copied' && <span data-testid={`job-link-copied-${jobId}`}>Link copiato</span>}
      </span>
      {esito === 'manual' && (
        <span className={styles.copyManual} onClick={(e) => e.stopPropagation()}>
          <label>
            Copia il link a mano:{' '}
            <input
              ref={manualRef}
              type="text"
              readOnly
              value={url}
              aria-label={`Link al job ${label}`}
              data-testid={`job-link-url-${jobId}`}
              onFocus={(e) => e.currentTarget.select()}
            />
          </label>
          <button type="button" onClick={() => setEsito('idle')} aria-label="Chiudi">
            ×
          </button>
        </span>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Pannello log (inline, sotto la riga; filtrabile per testo; live se `poll`)
// ---------------------------------------------------------------------------

function LivelloClass(level: string): string {
  switch (level.toUpperCase()) {
    case 'INFO':
      return styles.logLevelInfo ?? '';
    case 'WARNING':
      return styles.logLevelWarning ?? '';
    case 'ERROR':
      return styles.logLevelError ?? '';
    case 'CRITICAL':
      return styles.logLevelCritical ?? '';
    case 'DEBUG':
      return styles.logLevelDebug ?? '';
    default:
      return styles.logLevelDefault ?? '';
  }
}

function LogPanel({ runId, filtro, poll }: { runId: string; filtro: string; poll: boolean }) {
  const { data: logs, isLoading, isError } = usePrefectFlowRunLogs(runId, poll);
  const term = filtro.trim().toLowerCase();
  const righe = term
    ? (logs ?? []).filter((l) => l.message.toLowerCase().includes(term))
    : (logs ?? []);

  return (
    <div className={styles.logPanelInline} data-testid={`log-panel-${runId}`}>
      {poll && (
        <span className={styles.liveTag} data-testid={`live-tag-${runId}`}>
          LIVE
        </span>
      )}
      {isLoading && <p className={styles.stateMessage}>Caricamento log in corso...</p>}
      {isError && <p className={styles.errorMessage}>Errore nel caricamento dei log.</p>}
      {logs && (
        <div className={styles.logPanel}>
          {logs.length === 0 && (
            <span className={styles.stateMessage}>Nessun log disponibile.</span>
          )}
          {logs.length > 0 && righe.length === 0 && (
            <span className={styles.stateMessage}>Nessun log corrisponde al filtro «{term}».</span>
          )}
          {righe.map((line: LogLine, i: number) => (
            <div key={i} className={styles.logLine}>
              <span className={styles.logTimestamp}>{line.timestamp}</span>
              <span className={LivelloClass(line.level)}>{line.level}</span>
              <span className={styles.logMessage}>{line.message}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pulsante "Esegui ora" (sempre visibile; deny popup se non amministratore)
// ---------------------------------------------------------------------------

function RunNowButton({
  run,
  isAdmin,
  onDeny,
}: {
  run: FlowRun;
  isAdmin: boolean;
  onDeny: () => void;
}) {
  const { mutate, isPending, isSuccess, isError } = useRunDeployment();

  return (
    <button
      type="button"
      className={styles.runNowButton}
      data-testid={`btn-run-now-${run.id}`}
      disabled={isAdmin && (isPending || isSuccess)}
      onClick={(e) => {
        e.stopPropagation();
        if (!isAdmin) {
          onDeny();
          return;
        }
        mutate(run.deploymentId!);
      }}
      title="Avvia ora senza modificare lo schedule"
    >
      {isPending ? 'Avvio…' : isSuccess ? 'Avviato ✓' : isError ? 'Riprova' : 'Esegui ora'}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Dropdown multiselect con checkbox + select/deselect all
// ---------------------------------------------------------------------------

function MultiFlowFilter({
  options,
  selected,
  onToggle,
  onSelectAll,
  onDeselectAll,
}: {
  options: readonly string[];
  selected: ReadonlySet<string>;
  onToggle: (name: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const allSelected = selected.size === 0; // empty = "tutti" implicitamente

  return (
    <div className={styles.multiSelect} ref={ref} data-testid="filtro-flow">
      <button
        type="button"
        className={styles.multiSelectTrigger}
        aria-expanded={open}
        data-testid="filtro-flow-trigger"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        {allSelected ? 'Tutti i flow' : `${selected.size} selezionati`}
      </button>
      {open && (
        <div className={styles.multiSelectMenu} role="listbox" data-testid="filtro-flow-menu">
          <div className={styles.multiSelectActions}>
            <button
              type="button"
              className={styles.multiSelectActionBtn}
              data-testid="filtro-flow-select-all"
              onClick={(e) => {
                e.stopPropagation();
                onSelectAll();
              }}
            >
              Seleziona tutti
            </button>
            <button
              type="button"
              className={styles.multiSelectActionBtn}
              data-testid="filtro-flow-deselect-all"
              onClick={(e) => {
                e.stopPropagation();
                onDeselectAll();
              }}
            >
              Deseleziona tutti
            </button>
          </div>
          <ul className={styles.multiSelectList}>
            {options.map((name) => {
              const checked = selected.has(name);
              return (
                <li key={name}>
                  <label className={styles.multiSelectOption}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => onToggle(name)}
                      data-testid={`filtro-flow-option-${name}`}
                    />
                    <span>{name}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pagina principale
// ---------------------------------------------------------------------------

/** Etichetta "job type" usata dal filtro multi-flow. */
function jobLabel(run: JobRun): string | undefined {
  return run.deploymentName ?? run.flowName ?? run.name;
}

export function PrefectJobsPage() {
  const { claims } = useAuth();
  const isAdmin = claims?.roles.includes('admin') ?? false;

  const { data: runs, isLoading, isError, error } = usePrefectJobRuns();

  // Deep link /jobs#<jobId>: ogni job ha un ID stabile (slug del deployment, vedi job-links.ts).
  const location = useLocation();
  const linkedJobId = jobIdFromHash(location.hash);
  const pageHref = useHref(location.pathname);
  const jobIds = useMemo(() => jobIdsFor(runs ?? []), [runs]);
  const jobUrl = (jobId: string) => `${window.location.origin}${pageHref}#${jobId}`;
  const linkedRun = linkedJobId
    ? (runs ?? []).find((r) => jobIds.get(r.id) === linkedJobId)
    : undefined;

  // Log espandibili manualmente: insieme di run-id aperti.
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [denyVisible, setDenyVisible] = useState(false);

  // Filtri
  const [flowFilter, setFlowFilter] = useState<ReadonlySet<string>>(new Set());
  const [logSearch, setLogSearch] = useState('');

  function toggleRow(runId: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(runId)) next.delete(runId);
      else next.add(runId);
      return next;
    });
  }

  // Tutte le etichette dei job disponibili (per il dropdown).
  const jobLabels = useMemo(
    () =>
      Array.from(
        new Set((runs ?? []).map((r) => jobLabel(r)).filter((n): n is string => !!n)),
      ).sort(),
    [runs],
  );

  // Job linkato: appena compare in elenco (una volta per link) apre i log e, se un filtro lo
  // nasconde, azzera il filtro. Il polling ogni 2s non lo riapre se l'utente lo chiude.
  const linkApplicato = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!linkedJobId || !linkedRun || linkApplicato.current === linkedJobId) return;
    linkApplicato.current = linkedJobId;
    const label = jobLabel(linkedRun);
    setFlowFilter((prev) => (prev.size === 0 || (label && prev.has(label)) ? prev : new Set()));
    if (linkedRun.phase !== 'never') {
      setExpanded((prev) => (prev.has(linkedRun.id) ? prev : new Set(prev).add(linkedRun.id)));
    }
  }, [linkedJobId, linkedRun]);

  // Porta in vista la riga linkata appena è renderizzata (una volta per link).
  const scrollFatto = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!linkedJobId || !linkedRun || scrollFatto.current === linkedJobId) return;
    const riga = document.querySelector(`[data-job-id="${linkedJobId}"]`);
    if (!riga) return;
    scrollFatto.current = linkedJobId;
    riga.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  });

  const runsFiltrati = useMemo(() => {
    const sel = flowFilter;
    if (sel.size === 0) return runs ?? [];
    return (runs ?? []).filter((r) => {
      const label = jobLabel(r);
      return label != null && sel.has(label);
    });
  }, [runs, flowFilter]);

  return (
    <div>
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Scheduled Job</h1>
        <span className={styles.pageSubtitle}>
          Per ogni job: l&apos;ultima esecuzione (in corso o terminata) + i job mai eseguiti. I job
          in corso sono aggiornati live (polling 2s) con i log in tempo reale. Clicca il nome di un
          job per copiarne il link da condividere.
        </span>
      </div>

      {/* Barra filtri */}
      <div className={styles.filtersBar} data-testid="prefect-filters">
        <span className={styles.filterField}>
          Flow:
          <MultiFlowFilter
            options={jobLabels}
            selected={flowFilter}
            onToggle={(name) =>
              setFlowFilter((prev) => {
                const next = new Set(prev);
                if (next.has(name)) next.delete(name);
                else next.add(name);
                return next;
              })
            }
            onSelectAll={() => setFlowFilter(new Set(jobLabels))}
            onDeselectAll={() => setFlowFilter(new Set())}
          />
        </span>

        <label className={styles.filterField}>
          Log contengono:
          <input
            type="text"
            value={logSearch}
            onChange={(e) => setLogSearch(e.target.value)}
            placeholder="STRINGZ"
            data-testid="input-log-search"
          />
        </label>
      </div>

      {isLoading && <p className={styles.stateMessage}>Caricamento job in corso...</p>}
      {isError && (
        <p className={styles.errorMessage}>
          Errore nel caricamento dei job:{' '}
          {error instanceof Error ? error.message : 'errore sconosciuto'}
        </p>
      )}

      {runs && linkedJobId && !linkedRun && (
        <p className={styles.linkNotFound} role="status" data-testid="job-link-not-found">
          Il job «{linkedJobId}» del link non è presente in elenco (rinominato o rimosso?).
        </p>
      )}

      {runs && (
        <table className={styles.table} data-testid="flow-runs-table">
          <thead>
            <tr>
              <th>Flow</th>
              <th>Stato</th>
              <th>Eseguito / previsto</th>
              <th>Durata</th>
              <th>Prossima esecuzione</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {runsFiltrati.length === 0 && (
              <tr>
                <td colSpan={COL_COUNT} className={styles.stateMessage}>
                  {runs.length === 0 ? 'Nessun job trovato.' : 'Nessun job corrisponde ai filtri.'}
                </td>
              </tr>
            )}
            {runsFiltrati.map((run: JobRun) => {
              const isRunning = run.phase === 'running';
              // I job in corso mostrano sempre i log (live); gli altri rispondono al toggle manuale.
              const isOpen = isRunning || expanded.has(run.id);
              const isNever = run.phase === 'never';
              const jobId = jobIds.get(run.id) ?? run.id;
              const isLinked = jobId === linkedJobId;
              return (
                <Fragment key={run.id}>
                  <tr
                    className={`${styles.tableRowClickable} ${isOpen ? styles.tableRowSelected : ''} ${isRunning ? styles.tableRowRunning : ''} ${isLinked ? styles.tableRowLinked : ''}`}
                    onClick={() => !isNever && toggleRow(run.id)}
                    data-testid={`flow-run-row-${run.id}`}
                    data-job-id={jobId}
                    aria-current={isLinked ? 'location' : undefined}
                  >
                    <td>
                      <JobNameCell
                        name={run.flowName ?? run.deploymentName}
                        jobId={jobId}
                        url={jobUrl(jobId)}
                      />
                    </td>
                    <td>
                      <StateBadge stateType={run.stateType} label={run.state} />
                      {isRunning && (
                        <span data-testid={`phase-running-${run.id}`} className={styles.runningTag}>
                          in corso
                        </span>
                      )}
                      {isNever && (
                        <span
                          data-testid={`phase-never-${run.id}`}
                          style={{ marginLeft: '0.4rem', fontSize: '0.7rem', color: '#6c757d' }}
                        >
                          mai eseguito
                        </span>
                      )}
                    </td>
                    <td data-testid={`flow-run-esecuzione-${run.id}`}>
                      {isNever ? '-' : orarioEsecuzione(run)}
                    </td>
                    <td>{isNever ? '-' : durata(run.startTime, run.endTime)}</td>
                    <td data-testid={`flow-run-next-${run.id}`}>
                      {run.nextScheduledRun ? fmtDateTime(run.nextScheduledRun) : '-'}
                    </td>
                    <td>
                      {isEseguibileOnDemand(run) ? (
                        <RunNowButton
                          run={run}
                          isAdmin={isAdmin}
                          onDeny={() => setDenyVisible(true)}
                        />
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className={styles.logRow} data-testid={`log-row-${run.id}`}>
                      <td colSpan={COL_COUNT}>
                        <LogPanel runId={run.id} filtro={logSearch} poll={isRunning} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      {denyVisible && (
        <RunDenyPopup
          sub={claims?.sub}
          roles={claims?.roles ?? []}
          onClose={() => setDenyVisible(false)}
        />
      )}
    </div>
  );
}
